<template>
  <client-only>
    <!-- For Cloudinary URLs (with or without transformations) -->
    <nuxt-img
      v-if="isCloudinaryUrl"
      provider="cloudinary"
      :src="publicId"
      :modifiers="modifiers"
      :sizes="sizes"
      v-bind="$attrs"
    ></nuxt-img>

    <!-- For Contentful URLs - try Cloudinary first, fallback to original URL -->
    <nuxt-img
      v-else-if="isContentfulUrl && !cloudinaryFailed"
      provider="cloudinary"
      :src="cloudinaryPublicId"
      :modifiers="modifiers"
      :sizes="sizes"
      v-bind="$attrs"
      @error="handleCloudinaryError"
    ></nuxt-img>

    <!-- Fallback for Contentful URLs when Cloudinary fails -->
    <nuxt-img
      v-else-if="isContentfulUrl && cloudinaryFailed"
      :src="url"
      :sizes="sizes"
      v-bind="$attrs"
    ></nuxt-img>

    <!-- Fallback for other URLs -->
    <nuxt-img
      v-else
      src="https://res.cloudinary.com/djurensratt/image/upload/rdk/glada-kycklingar-utomhus.jpg"
      :sizes="sizes"
      v-bind="$attrs"
    ></nuxt-img>

    <template #fallback><img v-bind="$attrs" /></template>
  </client-only>
</template>

<script setup lang="ts">
import { convertContentfulToCloudinary, stripDeliveryTransformations } from '@/utils/cloudinary'

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    url: string
    width?: string | number | null
    height?: string | number | null
    dpr?: string
    crop?: string
    gravity?: string
    responsive?: string | null
    /** @nuxt/image sizes, e.g. `xs:100vw lg:50vw`; overrides the full-width default of `responsive`. */
    sizes?: string
    /** Width/height ratio to crop to at every srcset width, as a decimal (`1.7778`, not `16:9`). */
    aspect?: string
  }>(),
  {
    width: null,
    height: null,
    dpr: 'auto',
    crop: 'lfill',
    gravity: 'faces',
    responsive: null,
    sizes: undefined,
    aspect: undefined,
  },
)

const {
  public: { cloudinaryBaseUrl },
} = useRuntimeConfig()

// Check if this is a Cloudinary URL
const isCloudinaryUrl = computed(() => {
  return props.url.includes('cloudinary.com')
})

// Check if this is a Contentful URL
const isContentfulUrl = computed(() => {
  return props.url.includes('images.ctfassets.net')
})

// Extract public ID for Cloudinary URLs
const publicId = computed(() => {
  if (isCloudinaryUrl.value) {
    return stripDeliveryTransformations(props.url.replace(cloudinaryBaseUrl, ''))
  }
  return ''
})

// Convert Contentful URL to Cloudinary public ID
const cloudinaryPublicId = computed(() => {
  if (isContentfulUrl.value) {
    const cloudinaryUrl = convertContentfulToCloudinary(props.url)
    return stripDeliveryTransformations(cloudinaryUrl.replace(cloudinaryBaseUrl, ''))
  }
  return ''
})

const modifiers = computed(() => {
  const mods: Record<string, string> = {}

  if (props.width && props.width !== 'auto') {
    mods.w = props.width.toString()
  }
  if (props.height && props.height !== 'auto') {
    mods.h = props.height.toString()
  }
  if (props.crop) {
    mods.c = props.crop
  }
  if (props.gravity) {
    mods.g = props.gravity
  }
  if (props.dpr && props.dpr !== 'auto') {
    mods.dpr = props.dpr
  }
  if (props.aspect) {
    mods.ar = props.aspect
  }

  return mods
})

const sizes = computed(() => {
  if (props.sizes) return props.sizes
  return props.responsive === 'width'
    ? 'xs:100vw sm:100vw md:100vw lg:100vw xl:100vw 2xl:100vw 3xl:100vw'
    : undefined
})

// Track if Cloudinary image failed to load
const cloudinaryFailed = ref(false)

// Handle Cloudinary image load error
const handleCloudinaryError = () => {
  console.warn('Cloudinary image failed to load, falling back to Contentful URL:', props.url)
  cloudinaryFailed.value = true
}
</script>
